import React, {
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback
} from 'react';
import PropTypes from 'prop-types';
import { MapContext } from 'components/MapboxMap';
import { center, points } from '@turf/turf';
import { featureCollection } from '@turf/helpers';
import { useLocation } from 'react-router-dom';
import { debounce, isEmpty, isEqual, sortBy } from 'lodash';

import { Context } from 'components/Store';
import { getUpdatedBoundingBox } from 'screens/Property/helpers/propertyDataHelpers';
import { propertiesV3 as propertiesV3Api } from 'utilities/api';
import {
  MAP_LABEL_LOCATIONS,
  DEFAULT_ORG_MAP_VIEW,
  DEFAULT_ZOOM,
  LAST_COORDINATES_STORED,
  NATIONAL_ZOOM,
  PROPERTY_LANDING_MAP_CORDS,
  PROPERTY_CREATE_STATE,
  USA_CENTER_COORDINATES
} from 'screens/Property/helpers/constants';
import {
  SET_MAP_PRINTABLE_AREA_BBOX,
  IS_MAP_MOVING,
  SET_MAP_LEGEND_LABELS
} from 'reducers/reducer';
import {
  calculatePrintableAreaBoundingBox,
  getCenterOfBoundingBox,
  PRINTABLE_AREA_PADDING
} from 'screens/Property/helpers/mapApiHelpers';
import createAction from 'helpers/createAction';
import getAllCoordinates from 'screens/Property/PropertiesLanding/PropertyCreate/helpers.js/getAllCoordinates';
import PropertyMapContext from '../../PropertyMapContext';
import {
  buildCustomLabel,
  CUSTOM_LAYER_TYPE,
  FOCUSED_LAYER_TYPE,
  hasCustomLabel,
  isFeatureSelected,
  raiseLabelLayers,
  SELECTED_LAYER_TYPE
} from '../PropertyMap/utilities/mapboxLayerHelpers';
import SelectedLayer from './components/SelectedLayer/SelectedLayer';
import DynamicLayer from './components/DynamicLayer';
import CustomLayers from './components/CustomLayers';
import { editRegex, createRegex } from '../constants';
import { getSeasonIds, getFarmId } from './helpers/storageHelpers';
import handlePropertiesData from './helpers/dataHandlers';

const propertyFeatureLayerDefaultPropAllFFTGeoJson = { features: [] };
const propertyFeatureLayerDefaultPropSetZoom = () => {};
const propertyFeatureLayerDefaultPropOnFit = () => {};
const fieldCreateRegex = /\/app\/property-landing\/create\/?$/;
const hasInfiniteBound = bounds =>
  bounds.some(bound => bound === Infinity || bound === -Infinity);

// Leaving the export preview for the settings step with the same selection
// keeps the camera where the preview left it.
const isBackFromExportPreview = (
  mapPrintableAreaBbox,
  exportPDFMapsStep,
  fieldsToMassiveAssign
) =>
  mapPrintableAreaBbox?.prevStep === 1 &&
  exportPDFMapsStep === 0 &&
  isEqual(mapPrintableAreaBbox.fieldsToMassiveAssign, fieldsToMassiveAssign);

const pickFocusedFeatures = (
  geoJSON,
  { isMultipleSelect, isMassAssignerActive, fieldsToMassiveAssign }
) => {
  const selectedIdSet = new Set();
  fieldsToMassiveAssign.forEach(field => {
    selectedIdSet.add(field.id);
    field?.cropzones?.forEach(cz => selectedIdSet.add(cz.id));
  });

  return geoJSON?.features.filter(feature => {
    if (
      !isMultipleSelect &&
      !isMassAssignerActive &&
      fieldsToMassiveAssign.length === 0
    ) {
      return feature?.properties?.$layer === FOCUSED_LAYER_TYPE;
    }
    return selectedIdSet.has(feature?.properties?.id);
  });
};

// A fit answers "what is focused changed", not "it moved": imported shapes are
// focused, and every drag release on the create screen used to snap the map
// back to all of them. The ids are recorded only once the map has settled, so
// the refit that follows the initial view still happens.
const createRefitGuard = () => {
  let fittedShapeIds = null;
  return (fc, settled, boundingBox) => {
    const shapeIds = sortBy(
      (fc?.features ?? []).map(feature => feature.id ?? feature.properties?.id),
      String
    );
    const sameShapes = isEqual(shapeIds, fittedShapeIds);
    if (settled) fittedShapeIds = shapeIds;
    return sameShapes && Boolean(boundingBox);
  };
};

const PropertyFeatureLayer = ({
  geoJSON = null,
  customStyles = null,
  visible = false,
  setZoom = propertyFeatureLayerDefaultPropSetZoom,
  activeObjectType,
  isGeoJSONLabelEnabled,
  currentCoordinates = null,
  allFFTGeoJson = propertyFeatureLayerDefaultPropAllFFTGeoJson,
  showArchivedLabels = false,
  disableAutoFit = false,
  onFit = propertyFeatureLayerDefaultPropOnFit
}) => {
  const { pathname, state } = useLocation();

  const [
    {
      selectedProperty,
      loadingProperties,
      loadTimestamp,
      fieldsToMassiveAssign,
      isExportPDFMapsActive,
      isMassAssignerActive,
      exportPDFMapsStep,
      mapPrintableAreaBbox,
      cropColors,
      mapLabelConfig
    },
    dispatch
  ] = useContext(Context);
  const [boundingBox, setBoundingBox] = useState();
  const [mapIsReady, setMapIsReady] = useState(false);
  const isMultipleSelect = isExportPDFMapsActive;

  const isAnimateActive = useRef(false);
  // The pending background fit: null, or the farm to frame (all when unset).
  const pendingShapesFit = useRef(null);
  const revealWhenReady = useRef(false);
  const [shouldSkipRefit] = useState(createRefitGuard);

  const [filteredGeoJSON, setFilteredGeoJSON] = useState(null);

  useEffect(() => {
    if (
      (editRegex.test(pathname) || createRegex.test(pathname)) &&
      allFFTGeoJson?.features?.length
    ) {
      setFilteredGeoJSON({
        type: 'FeatureCollection',
        features: allFFTGeoJson.features.filter(feature => feature?.geometry)
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Fetch properties data when refreshing on create/edit pages
  useEffect(() => {
    const shouldFetchData =
      (createRegex.test(pathname) || editRegex.test(pathname)) &&
      !allFFTGeoJson?.features?.length &&
      !filteredGeoJSON;

    if (!shouldFetchData) return;

    const seasonIds = getSeasonIds();
    const farmId = getFarmId(state);

    const { promise } = propertiesV3Api.fetchByPost(
      { seasonIds, include_extended: true },
      { includeArchived: true },
      true,
      false
    );

    promise.then(({ data }) =>
      handlePropertiesData(data, farmId, setFilteredGeoJSON)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, state, allFFTGeoJson]);

  const selectedJSON = useMemo(
    () =>
      featureCollection(
        geoJSON?.features?.filter(
          ({ properties }) => properties.$layer === SELECTED_LAYER_TYPE
        ) || []
      ),
    [geoJSON]
  );

  const taggedGeoJSON = useMemo(() => {
    const sourceGeoJSON = filteredGeoJSON || geoJSON;
    if (
      !isMultipleSelect &&
      !isMassAssignerActive &&
      fieldsToMassiveAssign.length === 0
    ) {
      return sourceGeoJSON;
    }
    // Pre-compute O(1) lookup structures
    const selectedIdSet = new Set();
    const selectedFieldById = new Map();
    fieldsToMassiveAssign.forEach(field => {
      selectedIdSet.add(field.id);
      selectedFieldById.set(field.id, field);
      field.cropzones.forEach(cz => selectedIdSet.add(cz.id));
    });
    const customStyleKeys = new Set(Object.keys(customStyles ?? {}));

    // Start labelLegendKey at number of unique fields and cropzones + 1 to avoid displaying 0 to the user
    let labelLegendKey = fieldsToMassiveAssign.reduce((count, field) => {
      return count + (field.cropzones.length ? field.cropzones.length : 1);
    }, 1);
    const taggedFeatures = sourceGeoJSON.features
      .filter(
        ({ properties, geometry }) => properties?.id && geometry?.coordinates
      )
      .map(feature => {
        const featureProps = { ...feature.properties };
        const isSelected = selectedIdSet.has(featureProps.id);
        const isCustom = customStyleKeys.has(featureProps.$layer) || isSelected;
        const hasCrop = !!featureProps.crop?.id;

        if (featureProps.$landType === 'field') {
          const selectedField = selectedFieldById.get(featureProps.id);
          if (selectedField) {
            featureProps.farmId = selectedField.farmId;
            featureProps.farmName = selectedField.farmName;
          }
        }

        const customLabel = buildCustomLabel(
          featureProps,
          mapLabelConfig,
          selectedFieldById,
          exportPDFMapsStep === 0
        );

        if (hasCustomLabel(feature) && isSelected) {
          labelLegendKey -= 1;
        }

        return {
          ...feature,
          properties: {
            ...featureProps,
            cropColor:
              isCustom && hasCrop && exportPDFMapsStep === 1
                ? cropColors[featureProps.crop.id]
                : undefined,
            $layer: isCustom ? CUSTOM_LAYER_TYPE : featureProps.$layer,
            customLabel,
            labelLegendKey: hasCustomLabel(feature) ? labelLegendKey : undefined
          }
        };
      });

    return featureCollection(taggedFeatures);
  }, [
    filteredGeoJSON,
    geoJSON,
    customStyles,
    cropColors,
    isMultipleSelect,
    fieldsToMassiveAssign,
    exportPDFMapsStep,
    mapLabelConfig,
    isMassAssignerActive
  ]);

  const customJSONArr = useMemo(
    () =>
      Object.keys(customStyles ?? {}).map(key =>
        (taggedGeoJSON.features || []).filter(
          ({ properties }) =>
            properties.$layer === key || properties.$layer === CUSTOM_LAYER_TYPE
        )
      ),
    [taggedGeoJSON, customStyles]
  );

  const map = useContext(MapContext);

  // Readiness used to be checked only during render, so when the style was
  // still loading its sources at that moment nothing re-checked it: the export
  // preview sat unfitted until some option happened to re-render this. `idle`
  // fires once every source has loaded and no camera move is pending, which is
  // exactly when the check turns true.
  useEffect(() => {
    if (!map || mapIsReady) return undefined;
    const isSettled = () => !map.isMoving() && map.isStyleLoaded();
    if (isSettled()) {
      setMapIsReady(true);
      return undefined;
    }
    const markReadyWhenSettled = () => {
      if (isSettled()) setMapIsReady(true);
    };
    map.on('idle', markReadyWhenSettled);
    return () => {
      map.off('idle', markReadyWhenSettled);
    };
  }, [map, mapIsReady]);

  useEffect(() => {
    if (!map) return undefined;
    const keepLabelsOnTop = () => raiseLabelLayers(map);
    map.on('styledata', keepLabelsOnTop);
    return () => {
      map.off('styledata', keepLabelsOnTop);
    };
  }, [map]);

  useEffect(() => {
    if (!taggedGeoJSON?.features || exportPDFMapsStep !== 1) return;
    const newMapLegendLabels = {};
    taggedGeoJSON.features.forEach(feature => {
      if (
        hasCustomLabel(feature) &&
        isFeatureSelected(fieldsToMassiveAssign, feature)
      ) {
        newMapLegendLabels[feature.properties.labelLegendKey] =
          feature.properties.customLabel;
      }
    });
    createAction(dispatch, SET_MAP_LEGEND_LABELS, newMapLegendLabels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportPDFMapsStep, mapLabelConfig]);

  useEffect(() => {
    if (!map) return;
    const handleIdle = () => {
      if (!map.isMoving()) {
        if (pathname !== '/app/property-landing') return;
        const _center = map.getCenter();
        const zoom = map.getZoom();
        localStorage.setItem(
          DEFAULT_ORG_MAP_VIEW,
          JSON.stringify({ coordinates: [_center.lng, _center.lat], zoom })
        );
        map.off('idle', handleIdle);
      }
    };
    map.on('idle', handleIdle);
    // eslint-disable-next-line consistent-return
    return () => {
      map.off('idle', handleIdle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  const handleZooming = useCallback(
    bounds => {
      map.fitBounds(bounds, {
        padding: 160,
        animate: isAnimateActive.current
      });
      setBoundingBox(bounds);
      setZoom([map.getZoom()]);
      onFit();
    },
    [map, setZoom, isAnimateActive, onFit]
  );

  const isValidLngLat = coordinates =>
    (Array.isArray(coordinates) &&
      Number.isFinite(coordinates[0]) &&
      Number.isFinite(coordinates[1])) ||
    (!!coordinates &&
      Number.isFinite(coordinates.lat) &&
      (Number.isFinite(coordinates.lng) || Number.isFinite(coordinates.lon)));

  const flyToCoordinates = (coordinates, zoom) => {
    // Persisted view state (e.g. DEFAULT_ORG_MAP_VIEW in localStorage) can be
    // read on first paint before its coordinates are populated; flying to an
    // invalid LngLatLike throws inside mapbox mid-animation. Skip until the
    // coordinates are valid — a later effect re-flies once data is ready.
    if (!isValidLngLat(coordinates)) return;
    map.flyTo({
      center: coordinates,
      zoom,
      animate: false
    });
  };

  const handleSelectedProperty = (stateParam = state) => {
    const { parentFarm } = stateParam || [];
    const allCoordinates = getAllCoordinates(
      selectedProperty,
      parentFarm
    ).filter(coordinate => coordinate);
    if (!allCoordinates.length) return;
    const isNestedArray = Array.isArray(allCoordinates[0][0]);
    const processedCoordinates = isNestedArray
      ? allCoordinates.flat(1)
      : allCoordinates;
    const featureCollection_ = {
      type: 'FeatureCollection',
      features: processedCoordinates.map(coord => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: coord
        }
      }))
    };

    const bounds = getUpdatedBoundingBox(undefined, featureCollection_, false);
    map.fitBounds(bounds, {
      padding: 160,
      animate: isAnimateActive.current
    });
    setBoundingBox(bounds);
    setZoom([map.getZoom()]);
    onFit();
  };

  const handleDefaultOrgView = () => {
    const defaultView = JSON.parse(localStorage.getItem(DEFAULT_ORG_MAP_VIEW));
    if (defaultView) {
      flyToCoordinates(defaultView.coordinates, defaultView.zoom);
    } else {
      flyToCoordinates(USA_CENTER_COORDINATES, NATIONAL_ZOOM);
    }
  };

  const handleEmptyFarmSelected = isPropertyLanding => {
    // has fields loaded from previous selections
    if (selectedProperty?.type === 'Farm' && !isPropertyLanding) {
      const lastMap = localStorage.getItem(PROPERTY_LANDING_MAP_CORDS);
      if (lastMap) {
        const { coordinates, zoom } = JSON.parse(lastMap);
        flyToCoordinates(coordinates, zoom);
        return true;
      }
    }
    return false;
  };

  const handleNewOrgView = () => {
    const noLayers = (filteredGeoJSON || geoJSON).features.length === 0;
    if (
      state?.isNewOrg &&
      Object.keys(selectedProperty).length === 0 &&
      noLayers &&
      currentCoordinates
    ) {
      if (
        isEqual(
          currentCoordinates?.coordinates ?? currentCoordinates,
          USA_CENTER_COORDINATES
        ) &&
        !currentCoordinates?.userGeolocation
      ) {
        flyToCoordinates(USA_CENTER_COORDINATES, NATIONAL_ZOOM);
      } else if (
        currentCoordinates?.coordinates &&
        currentCoordinates?.userGeolocation
      ) {
        flyToCoordinates(currentCoordinates.userGeolocation, DEFAULT_ZOOM);
      }
      return true;
    }
    return false;
  };

  const getEffectiveState = () => {
    if (state) return state;

    if (createRegex.test(pathname) || editRegex.test(pathname)) {
      const savedState = localStorage.getItem(PROPERTY_CREATE_STATE);
      if (savedState) {
        try {
          return JSON.parse(savedState);
        } catch {
          return null;
        }
      }
    }
    return null;
  };

  const handleParentFarmFields = effectiveState => {
    const farmFieldsGeoJSON = {
      type: 'FeatureCollection',
      features: effectiveState.parentFarm.fields
        .filter(field => field?.geometry)
        .map(field => ({
          type: 'Feature',
          geometry: field.geometry,
          properties: {
            ...field,
            id: field.id,
            name: field.name,
            $landType: 'field',
            propertyId: effectiveState.parentFarm.id,
            cropzones: field.cropzones || []
          }
        }))
    };
    setFilteredGeoJSON(farmFieldsGeoJSON);

    const bounds = getUpdatedBoundingBox(
      undefined,
      {
        type: 'FeatureCollection',
        features: effectiveState.parentFarm.fields
          .filter(f => f.geometry?.coordinates && f.geometry?.type)
          .map(f => ({
            geometry: f.geometry
          }))
      },
      false
    );
    map.fitBounds(bounds, {
      padding: 160,
      animate: isAnimateActive.current
    });
    setBoundingBox(bounds);
    setZoom([map.getZoom()]);
    onFit();
  };

  const handleMapCoordinates = effectiveState => {
    flyToCoordinates(
      effectiveState.mapCoordinates.coordinates,
      effectiveState.mapCoordinates.zoom || DEFAULT_ZOOM[0]
    );
  };

  const processEffectiveState = effectiveState => {
    if (!effectiveState) return false;

    if (effectiveState?.parentFarm?.fields?.length) {
      handleParentFarmFields(effectiveState);
      return true;
    }

    if (effectiveState?.mapCoordinates) {
      handleMapCoordinates(effectiveState);
      return true;
    }

    if (selectedProperty?.fields?.length || selectedProperty?.fieldId) {
      handleSelectedProperty(effectiveState);
      return true;
    }

    return false;
  };

  const handleInitialMapView = (
    noLayers,
    isPropertyLanding,
    effectiveState
  ) => {
    const hasHandledNewOrg = handleNewOrgView();
    if (hasHandledNewOrg) return;

    if (
      !effectiveState?.isNewOrg &&
      Object.keys(selectedProperty).length === 0
    ) {
      handleDefaultOrgView();
      return;
    }

    const hasHandledEmptyFarm = handleEmptyFarmSelected(isPropertyLanding);
    if (hasHandledEmptyFarm) return;

    if (noLayers) {
      flyToCoordinates(USA_CENTER_COORDINATES, NATIONAL_ZOOM);
    }
  };

  useEffect(() => {
    // Consumers that drive the camera themselves opt out of the initial-view
    // fly-to. Otherwise this mount effect flies to the stored org/default view
    // (e.g. the property-landing map's last localStorage camera), overriding
    // the consumer's own framing.
    if (disableAutoFit) return;
    if (isExportPDFMapsActive) return;

    const noLayers = (filteredGeoJSON || geoJSON).features.length === 0;
    const isPropertyLanding = pathname === '/app/property-landing';

    if (isPropertyLanding && loadingProperties) return;
    if (isPropertyLanding && noLayers) {
      flyToCoordinates(USA_CENTER_COORDINATES, NATIONAL_ZOOM);
      return;
    }

    const effectiveState = getEffectiveState();
    const setInitialView = () => {
      // "Save and Draw a new Field" from a crop zone asks for its farm to be
      // framed; arriving with no landing state at all frames every shape.
      const frameFarmId = effectiveState?.frameFarmId;
      const framesShapes =
        fieldCreateRegex.test(pathname) && (!effectiveState || frameFarmId);
      if (framesShapes) {
        pendingShapesFit.current = { farmId: frameFarmId };
        if (allFFTGeoJson?.features?.some(feature => feature?.geometry)) return;
      } else if (processEffectiveState(effectiveState)) return;

      if (loadingProperties) return;

      handleInitialMapView(noLayers, isPropertyLanding, effectiveState);
    };
    setInitialView();

    // With no shapes of its own (a new field) the owner has no fit to wait
    // for: the initial view is its framing.
    if (!geoJSON?.features?.length && !pendingShapesFit.current) {
      revealWhenReady.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mapIsReady || !revealWhenReady.current) return;
    revealWhenReady.current = false;
    onFit();
  }, [mapIsReady, onFit]);

  useEffect(() => {
    if (!pendingShapesFit.current || !map || !mapIsReady) return;
    const shapes = filteredGeoJSON?.features?.filter(
      feature => feature?.geometry?.coordinates
    );
    if (!shapes?.length) return;
    const { farmId } = pendingShapesFit.current;
    const farmShapes = shapes.filter(
      feature => feature.properties?.propertyId === farmId
    );
    const bounds = getUpdatedBoundingBox(
      undefined,
      featureCollection(farmShapes.length ? farmShapes : shapes),
      false
    );
    if (!bounds || hasInfiniteBound(bounds)) return;
    pendingShapesFit.current = null;
    handleZooming(bounds);
  }, [filteredGeoJSON, map, mapIsReady, handleZooming]);

  // Clean up localStorage when leaving create/edit pages
  useEffect(() => {
    return () => {
      if (createRegex.test(pathname) || editRegex.test(pathname)) {
        localStorage.removeItem(PROPERTY_CREATE_STATE);
      }
    };
  }, [pathname]);

  // adjust the bounding box so that the current zoom level can be determined
  useEffect(() => {
    // Opt-out for consumers that drive the camera themselves: this effect
    // re-fits the map on every geoJSON/selection change, which would fight
    // their own pan/zoom.
    if (disableAutoFit) return;

    const focusedGeoJSONFeatures = pickFocusedFeatures(geoJSON, {
      isMultipleSelect,
      isMassAssignerActive,
      fieldsToMassiveAssign
    });

    const fc = focusedGeoJSONFeatures.length
      ? featureCollection(focusedGeoJSONFeatures)
      : geoJSON;

    const settled = mapIsReady && !map.isMoving();
    if (shouldSkipRefit(fc, settled, boundingBox)) return;

    const newBounds = getUpdatedBoundingBox(
      boundingBox,
      fc,
      focusedGeoJSONFeatures.length
    );

    if (fc && newBounds && settled && !hasInfiniteBound(newBounds)) {
      const newLng = newBounds.slice(0, 2);
      const newLat = newBounds.slice(-2);

      const feature = points([newLng, newLat]);
      const centroid = center(feature);

      localStorage.setItem(
        LAST_COORDINATES_STORED,
        JSON.stringify(centroid.geometry.coordinates)
      );

      if (
        isExportPDFMapsActive &&
        (exportPDFMapsStep === 1 || !boundingBox) &&
        mapPrintableAreaBbox
      ) {
        if (!isEqual(mapPrintableAreaBbox.bbox, boundingBox)) {
          const oldContainer = map.getContainer();
          const newWidth = oldContainer.clientWidth - PRINTABLE_AREA_PADDING;
          const oldWidth =
            mapPrintableAreaBbox.containerWidth - PRINTABLE_AREA_PADDING;
          const oldZoom = map.getZoom();
          // Calculate the zoom offset based on the width ratio
          const zoomOffset = Math.log2(newWidth / oldWidth);
          const newZoom = oldZoom + zoomOffset;

          map.jumpTo({
            center: getCenterOfBoundingBox(mapPrintableAreaBbox.bbox),
            zoom: newZoom
          });
          setBoundingBox(mapPrintableAreaBbox.bbox);
        }
      } else if (
        !isBackFromExportPreview(
          mapPrintableAreaBbox,
          exportPDFMapsStep,
          fieldsToMassiveAssign
        )
      ) {
        handleZooming(newBounds);
      }
    }
  }, [
    boundingBox,
    filteredGeoJSON,
    geoJSON,
    map,
    mapIsReady,
    isMultipleSelect,
    fieldsToMassiveAssign,
    taggedGeoJSON?.features,
    isExportPDFMapsActive,
    exportPDFMapsStep,
    mapPrintableAreaBbox,
    isMassAssignerActive,
    handleZooming,
    disableAutoFit,
    shouldSkipRefit
  ]);

  useEffect(() => {
    if (loadingProperties) {
      setBoundingBox(undefined);
    }
  }, [loadingProperties, loadTimestamp]);

  // Every tree click stores a fresh selection object, a repeat click included.
  // Forgetting the last fit makes the fit run again, so a click on the shape
  // already selected brings the map back to it after a manual pan. An empty
  // selection (bulk actions, export, organisation switch) is left alone.
  useEffect(() => {
    if (!isEmpty(selectedProperty)) setBoundingBox(undefined);
  }, [selectedProperty]);

  useEffect(() => {
    // to make sure the last map position is updated the the user changes the selected property without moving or zooming the map
    if (!map || !selectedProperty) return;
    const zoom = map.getZoom();
    const _center = map.getCenter();
    const coordinates = [_center.lng, _center.lat];

    localStorage.setItem(
      PROPERTY_LANDING_MAP_CORDS,
      JSON.stringify({ coordinates, zoom })
    );
  }, [map, selectedProperty]);

  // wait for the bounding box movement to stop then set zoom to the map's current zoom value
  useEffect(() => {
    let mounted = true;

    const handleMove = debounce(async () => {
      // await set the zoom based on the map's new zoom level
      setZoom(() => {
        const newZoom = map.getZoom();
        return [newZoom];
      });
      if (exportPDFMapsStep !== 1) {
        const mapWidth = map.getContainer().clientWidth;
        createAction(dispatch, SET_MAP_PRINTABLE_AREA_BBOX, {
          containerWidth: mapWidth,
          bbox: calculatePrintableAreaBoundingBox(map),
          prevStep: 1,
          fieldsToMassiveAssign
        });
      } else {
        createAction(dispatch, SET_MAP_PRINTABLE_AREA_BBOX, {
          ...mapPrintableAreaBbox,
          prevStep: 0
        });
      }
      createAction(dispatch, IS_MAP_MOVING, map.isMoving());
    }, 50);

    const moveChecker = debounce(() => {
      if (mounted) {
        setZoom(() => {
          const newZoom = map.getZoom();
          return [newZoom];
        });
        const zoom = map.getZoom();
        const _center = map.getCenter();
        const coordinates = [_center.lng, _center.lat];

        localStorage.setItem(
          PROPERTY_LANDING_MAP_CORDS,
          JSON.stringify({ coordinates, zoom })
        );
        if (isExportPDFMapsActive) {
          createAction(dispatch, IS_MAP_MOVING, false);
        }
      }
    }, 300);

    if (map && pathname === '/app/property-landing') {
      map.on('moveend', moveChecker);
    }
    if (map && isExportPDFMapsActive && exportPDFMapsStep === 0) {
      map.on('move', handleMove);
      map.on('drag', handleMove);
      map.on('dragend', handleMove);
      map.on('zoom', handleMove);
    }

    return () => {
      mounted = false;
      if (map) {
        map.off('moveend', moveChecker);
        map.off('move', handleMove);
        map.off('drag', handleMove);
        map.off('zoom', handleMove);
      }
      moveChecker.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    boundingBox,
    setZoom,
    pathname,
    dispatch,
    exportPDFMapsStep,
    isExportPDFMapsActive
  ]);

  let labelKey;
  if (isMultipleSelect) {
    labelKey = 'name';
  } else if (activeObjectType === 'field' || activeObjectType === 'cropzone') {
    labelKey = 'fieldAndCropZoneLabel';
  } else {
    labelKey = 'defaultLabel';
  }

  const showLegendLabels =
    exportPDFMapsStep === 1 &&
    mapLabelConfig.labelLocation === MAP_LABEL_LOCATIONS.LEGEND;

  if ((!geoJSON && !filteredGeoJSON) || !visible) {
    return null;
  }
  return (
    <PropertyMapContext.Consumer>
      {({ onClickShape }) => (
        <>
          <SelectedLayer onClickShape={onClickShape} geoJSON={selectedJSON} />
          <DynamicLayer
            onClickShape={onClickShape}
            geoJSON={taggedGeoJSON}
            labelKey={labelKey}
            isGeoJSONLabelEnabled={isGeoJSONLabelEnabled}
            showArchivedLabels={showArchivedLabels}
          />
          {customStyles && !createRegex.test(pathname) && (
            <CustomLayers
              geoJSONArr={customJSONArr}
              stylesArr={Object.values(customStyles)}
              onClickShape={onClickShape}
              isMultipleSelect={isMultipleSelect}
              showLegendLabels={showLegendLabels}
            />
          )}
        </>
      )}
    </PropertyMapContext.Consumer>
  );
};

PropertyFeatureLayer.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  geoJSON: PropTypes.object,
  customStyles: PropTypes.objectOf(
    PropTypes.shape({
      fillColor: PropTypes.string,
      fillOutlineColor: PropTypes.string
    })
  ),
  visible: PropTypes.bool,
  setZoom: PropTypes.func,
  activeObjectType: PropTypes.string.isRequired,
  isGeoJSONLabelEnabled: PropTypes.bool.isRequired,
  allFFTGeoJson: PropTypes.shape(),
  currentCoordinates: PropTypes.shape({
    coordinates: PropTypes.arrayOf(PropTypes.number),
    userGeolocation: PropTypes.arrayOf(PropTypes.number)
  }),
  showArchivedLabels: PropTypes.bool,
  disableAutoFit: PropTypes.bool,
  onFit: PropTypes.func
};

export default PropertyFeatureLayer;
